import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material';
import { useCallback, useState, useEffect, useMemo } from 'react';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import {
  GetPointOfInterestGroupedByWorkpackDocument,
  useAllWorkpacksQuery,
  useAllWorkpackTemplatesForPoiContainerQuery,
  useCreateWorkpackMutation,
  useGroupPointOfInterestsByWorkpackIdMutation,
  WorkpackType,
  WorkpackWbs,
} from '@/__generated__/graphql';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { primary } from '@/theme/colors';
import { formatEnumForDisplay, hexColorWithAlpha } from '@/utils';
import { PointOfInterestsDocument } from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { LoadingButton } from '@/components/shared/LoadingButton';
import { MoveToWorkpackDialog } from './MoveToWorkpackDialog';

type AddToWorkpackDialogProps = {
  isOpen: boolean;
  handleWorkpackDialogClose: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  selectedPointOfInterestIds: string[];
  filtersApplied: boolean;
  refetchPointOfInterests: () => void;
  viewBy?: 'workpack' | 'poi' | 'workflow';
};

type RadioOptions = 'createNew' | 'moveToExisting';

const workpackRadioOptions = [
  { id: 1, label: 'Create New', value: 'createNew' },
  { id: 2, label: 'Move to Existing', value: 'moveToExisting' },
];

const WBS_FORMAT_FILTER: Record<WorkpackWbs | typeof NOT_APPLICABLE_LABEL, string> = {
  [WorkpackWbs.Fm]: 'FM',
  [WorkpackWbs.Other]: 'Other',
  [NOT_APPLICABLE_LABEL]: NOT_APPLICABLE_LABEL,
};

export const AddToWorkpackDialog = ({
  isOpen,
  handleWorkpackDialogClose,
  title,
  selectedPointOfInterestIds,
  filtersApplied,
  refetchPointOfInterests,
  viewBy,
}: AddToWorkpackDialogProps) => {
  const [selectedOption, setSelectedOption] = useState<RadioOptions>('createNew');
  const [workpackNumber, setWorkpackNumber] = useState<string>('');
  const [workpackType, setWorkpackType] = useState<WorkpackType | undefined | null>(undefined);
  const [workpackWbs, setWorkpackWbs] = useState<WorkpackWbs | undefined>(undefined);
  const [allWorkpacks, setAllWorkpacks] = useState<{ id: string; name: string }[]>([]);
  const [selectedWorkpackId, setSelectedWorkpackId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [openMoveToWorkpackDialog, setOpenMoveToWorkpackDialog] = useState<boolean>(false);

  const structureId = useGetStructureId();
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId() ?? '';

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const handleRadioOptionsChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, value: RadioOptions) => {
      setSelectedOption(value);
    },
    []
  );

  const [createWorkpackMutation] = useCreateWorkpackMutation();
  const [groupPointOfInterestsByWorkpackIdMutation, { loading }] =
    useGroupPointOfInterestsByWorkpackIdMutation({
      refetchQueries: [
        PointOfInterestsDocument,
        'GetPointOfInterest', // This will ensure the POI details are refetched
      ],
    });

  const movePoisToWorkpack = useCallback(
    async (id: string) => {
      await groupPointOfInterestsByWorkpackIdMutation({
        variables: {
          input: {
            workpackId: id,
            pointOfInterestIds: selectedPointOfInterestIds,
          },
        },
        refetchQueries: [GetPointOfInterestGroupedByWorkpackDocument],
        onCompleted: () => {
          handleWorkpackDialogClose(false);
          refetchPointOfInterests();
          showSnackBar('POIs have been moved successfully', 'success');
        },
        onError: (error) => {
          showSnackBar(`${error.message}`, 'error');
        },
      });
    },
    [
      refetchPointOfInterests,
      groupPointOfInterestsByWorkpackIdMutation,
      handleWorkpackDialogClose,
      selectedPointOfInterestIds,
      showSnackBar,
    ]
  );

  const { data: workpackTemplateData } = useAllWorkpackTemplatesForPoiContainerQuery({
    variables: {
      input: {
        assemblyTemplateId: selectedAssemblyTemplateId || '',
        structureId,
      },
    },
    fetchPolicy: 'cache-first',
    skip: !selectedAssemblyTemplateId || !structureId,
  });

  const handleCreateWorkpack = useCallback(() => {
    const workpackTemplateId = workpackTemplateData?.allWorkpackTemplates?.[0]?.id ?? '';
    createWorkpackMutation({
      variables: {
        input: {
          structureId,
          name: workpackNumber,
          type: workpackType,
          wbs: workpackWbs,
          workpackTemplateId,
        },
      },
    })
      .then(({ data }) => {
        if (data?.createWorkpack) {
          const workpackId = data?.createWorkpack.id;
          movePoisToWorkpack(workpackId);
        }
      })
      .catch((error) => {
        showSnackBar(`${error.message}`, 'error');
      });
  }, [
    createWorkpackMutation,
    movePoisToWorkpack,
    showSnackBar,
    structureId,
    workpackNumber,
    workpackTemplateData?.allWorkpackTemplates,
    workpackType,
    workpackWbs,
  ]);

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    [setSearchTerm]
  );

  const handleSelectedWorkpack = useCallback((id: string) => {
    setSelectedWorkpackId(id);
  }, []);

  const { data: allWorkpacksData } = useAllWorkpacksQuery({
    variables: {
      structureId
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    setAllWorkpacks(
      allWorkpacksData?.allWorkpacks.map(({ id, name }) => ({
        id: id,
        name: name,
      })) ?? []
    );
  }, [allWorkpacksData?.allWorkpacks]);

  const filteredWorkpacks = useMemo(() => {
    if (searchTerm !== '') {
      return allWorkpacks?.filter((workpack) => {
        return workpack.name.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
    return allWorkpacks;
  }, [allWorkpacks, searchTerm]);

  useEffect(() => {
    setOpenMoveToWorkpackDialog(
      filtersApplied && selectedPointOfInterestIds?.length > 0 && isOpen && viewBy === 'workpack'
    );
  }, [filtersApplied, isOpen, selectedPointOfInterestIds?.length, viewBy]);

  useEffect(() => {
    if (!isOpen) {
      setWorkpackNumber('');
    }
  }, [isOpen]);

  if (openMoveToWorkpackDialog) {
    return (
      <MoveToWorkpackDialog
        isOpen={openMoveToWorkpackDialog}
        handleWorkpackDialogClose={setOpenMoveToWorkpackDialog}
        handleCloseAddDialog={handleWorkpackDialogClose}
        title="Some POIs are not visible!"
        selectedPointOfInterestsLength={selectedPointOfInterestIds?.length}
      />
    );
  }

  return (
    <Dialog open={isOpen} maxWidth="sm" fullWidth sx={{ radius: 2 }}>
      <DialogTitle sx={{ m: 0, p: 2 }}>{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => handleWorkpackDialogClose(false)}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <RadioGroup
          row
          value={selectedOption}
          onChange={(event, value) => handleRadioOptionsChange(event, value as RadioOptions)}
        >
          {workpackRadioOptions.map((option) => (
            <FormControlLabel
              key={option.id}
              value={option.value}
              control={<Radio />}
              label={option.label}
            />
          ))}
        </RadioGroup>
        <Box sx={{ pt: 1, pb: 10 }}>
          {selectedOption === 'createNew' && (
            <>
              <TextField
                fullWidth
                label="Workpack number"
                variant="standard"
                value={workpackNumber}
                onChange={(event) => setWorkpackNumber(event?.target.value)}
              />
              <Autocomplete
                sx={{ pt: 3 }}
                value={workpackType}
                options={[NOT_APPLICABLE_LABEL, ...Object.values(WorkpackType)]}
                getOptionLabel={(option) => {
                  return formatEnumForDisplay(option);
                }}
                onChange={(_, newValue) =>
                  setWorkpackType(
                    newValue === NOT_APPLICABLE_LABEL ? undefined : (newValue as WorkpackType)
                  )
                }
                renderInput={(params) => (
                  <TextField {...params} label="Workpack type" variant="standard" />
                )}
              />
              <Autocomplete
                sx={{ pt: 3 }}
                value={workpackWbs}
                options={[...Object.values(WorkpackWbs), NOT_APPLICABLE_LABEL]}
                getOptionLabel={(option) =>
                  WBS_FORMAT_FILTER[option as keyof typeof WBS_FORMAT_FILTER]
                }
                onChange={(_, newValue) =>
                  setWorkpackWbs(
                    newValue === NOT_APPLICABLE_LABEL ? undefined : (newValue as WorkpackWbs)
                  )
                }
                renderInput={(params) => <TextField {...params} label="WBS" variant="standard" />}
              />
            </>
          )}
          {selectedOption === 'moveToExisting' && (
            <Stack direction="column">
              <TextField
                data-testId="search-field"
                fullWidth
                label="Search workpack"
                placeholder={searchTerm}
                variant="standard"
                sx={{ '.MuiInputBase-input': { ml: 1 } }}
                onChange={handleSearch}
              />
              <List
                sx={{
                  borderTop: 1,
                  borderColor: 'divider',
                  width: '100%',
                  bgcolor: 'background.paper',
                  position: 'relative',
                  overflow: 'auto',
                  maxHeight: 300,
                  '& ul': { padding: 0 },
                }}
              >
                {filteredWorkpacks.map((workpack) => (
                  <ListItem key={workpack.id} disableGutters>
                    <ListItemButton
                      sx={{ '&:hover': { background: hexColorWithAlpha(primary.light, 20) } }}
                      onClick={() => handleSelectedWorkpack(workpack.id)}
                    >
                      <ListItemIcon>
                        <Radio edge="start" checked={selectedWorkpackId === workpack.id} />
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{ fontSize: '1.4rem', fontWeight: '600' }}
                        primary={workpack.name}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Stack>
          )}
        </Box>
      </DialogContent>
      <Divider />
      <Divider orientation="horizontal" />
      <DialogActions>
        <Button
          id="add-to-workpack-cancel"
          onClick={() => handleWorkpackDialogClose(false)}
          color="primary"
          size="large"
        >
          Cancel
        </Button>
        {selectedOption === 'createNew' && (
          <LoadingButton onClick={handleCreateWorkpack} loading={loading}>
            Create & Move
          </LoadingButton>
        )}
        {selectedOption === 'moveToExisting' && (
          <LoadingButton
            disabled={filteredWorkpacks.length === 0}
            onClick={() => movePoisToWorkpack(selectedWorkpackId)}
            loading={loading}
          >
            Move
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
